/**
 * ExcelPreviewDialog — Componente reutilizável para pré-visualização de dados no estilo Excel.
 * Aceita múltiplas abas (sheets), cada uma com headers e rows.
 */
import { useState, useMemo, useCallback } from 'react';
import {
  Download,
  FileSpreadsheet,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface PreviewSheet {
  key: string;
  label: string;
  color: string;
  bg: string;
  headers: string[];
  rows: Record<string, string | number>[];
}

interface ExcelPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sheets: PreviewSheet[];
  filename: string;
  onDownload: () => void;
  isDownloading?: boolean;
  /** Optional cell renderer per column (by header name) */
  cellRenderer?: (header: string, value: string | number, row: Record<string, string | number>) => React.ReactNode;
}

const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
function colLetters(count: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    if (i < 26) out.push(ALPHA[i]);
    else out.push(ALPHA[Math.floor(i / 26) - 1] + ALPHA[i % 26]);
  }
  return out;
}

export function ExcelPreviewDialog({
  open,
  onOpenChange,
  sheets,
  filename,
  onDownload,
  isDownloading = false,
  cellRenderer,
}: ExcelPreviewDialogProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  const sheet = sheets[activeIdx] ?? sheets[0];
  const headers = sheet?.headers ?? [];
  const letters = useMemo(() => colLetters(headers.length), [headers.length]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return sheet?.rows ?? [];
    const term = searchTerm.toLowerCase();
    return (sheet?.rows ?? []).filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(term))
    );
  }, [sheet, searchTerm]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredRows, page],
  );

  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);

  const handleSheetChange = useCallback((idx: number) => {
    setActiveIdx(idx);
    setSearchTerm('');
    setPage(0);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] h-[95vh] p-0 gap-0 flex flex-col overflow-hidden [&>button:last-child]:hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-[#217346] text-white shrink-0">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5" />
            <span className="font-medium text-sm">{filename}</span>
            <Badge variant="secondary" className="bg-white/20 text-white text-xs hover:bg-white/30">
              Pré-visualização
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={onDownload}
              disabled={isDownloading}
              className="gap-1.5 bg-white/20 hover:bg-white/30 text-white border-0 h-8"
            >
              <Download className="h-3.5 w-3.5" />
              {isDownloading ? 'Baixando...' : 'Baixar'}
            </Button>
            <DialogClose asChild>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f3f3f3] dark:bg-zinc-800 border-b shrink-0">
          <div className="flex items-center gap-1 bg-white dark:bg-zinc-900 border rounded px-2 py-1 flex-1 max-w-md">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              placeholder="Buscar na planilha..."
              className="text-sm bg-transparent border-0 outline-none flex-1 min-w-0"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {filteredRows.length.toLocaleString('pt-BR')} registros
            {searchTerm && ` (filtrados de ${(sheet?.rows?.length ?? 0).toLocaleString('pt-BR')})`}
          </span>
        </div>

        {/* Table body */}
        <div className="flex-1 overflow-auto bg-white dark:bg-zinc-950">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#e8e8e8] dark:bg-zinc-700 border-b">
                <th className="w-12 min-w-[48px] border-r border-b bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-0.5" />
                {letters.map((letter) => (
                  <th key={letter} className="border-r border-b bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-0.5 min-w-[80px]">
                    {letter}
                  </th>
                ))}
              </tr>
              <tr className="bg-[#4472C4] text-white">
                <th className="w-12 min-w-[48px] border-r border-b bg-[#365FA0] text-center text-[10px] py-1.5 font-medium">1</th>
                {headers.map((header, i) => (
                  <th key={i} className="border-r border-b px-2 py-1.5 text-left font-semibold whitespace-nowrap text-[11px]">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, idx) => {
                const rowNum = page * PAGE_SIZE + idx + 2;
                const isEven = idx % 2 === 0;
                return (
                  <tr
                    key={idx}
                    className={cn(
                      'hover:bg-blue-50 dark:hover:bg-blue-950/30 border-b',
                      isEven ? 'bg-[#D9E2F3] dark:bg-zinc-900/50' : 'bg-white dark:bg-zinc-950'
                    )}
                  >
                    <td className="w-12 min-w-[48px] border-r bg-[#e8e8e8] dark:bg-zinc-700 text-center text-[10px] text-muted-foreground py-1 font-medium">
                      {rowNum}
                    </td>
                    {headers.map((header, i) => {
                      const value = row[header] ?? '';
                      const rendered = cellRenderer ? cellRenderer(header, value, row) : null;
                      return (
                        <td key={i} className="border-r px-2 py-1 whitespace-nowrap max-w-[350px] truncate" title={String(value)}>
                          {rendered ?? String(value)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#f3f3f3] dark:bg-zinc-800 border-t shrink-0">
            <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
              ← Anterior
            </Button>
            <span className="text-xs text-muted-foreground">Página {page + 1} de {totalPages}</span>
            <Button size="sm" variant="ghost" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
              Próxima →
            </Button>
          </div>
        )}

        {/* Sheet tabs */}
        <div className="flex items-center gap-0 border-t bg-[#e8e8e8] dark:bg-zinc-800 shrink-0 overflow-x-auto">
          {sheets.map((s, i) => (
            <button
              key={s.key}
              onClick={() => handleSheetChange(i)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium border-r whitespace-nowrap transition-colors',
                activeIdx === i
                  ? `${s.bg} border-t-2 ${s.color}`
                  : 'bg-[#f3f3f3] dark:bg-zinc-700 text-muted-foreground hover:bg-white dark:hover:bg-zinc-600 border-t-2 border-t-transparent'
              )}
            >
              {s.label}
              <span className="ml-1.5 text-[10px] opacity-70">
                ({s.rows.length.toLocaleString('pt-BR')})
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
